import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import Card from "../../components/Card";
import { Badge, EtapeIcon, fmtDate, PHASE_STATUS, validatedByLabel } from "../../lib/helpers";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import AuthFileLink from "../../components/AuthFileLink";

export default function CollabProjectDetail() {
  const { cid, pid } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const load = useCallback(() => api.get(`/collab/projects/${pid}`).then((r) => setP(r.data)).catch(() => nav(`/clients/${cid}`)), [pid, cid, nav]);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;

  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/collab/projects/${pid}/files`, fd); toast.success("Fichier téléversé"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Échec du téléversement"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div className="max-w-4xl">
      <button onClick={() => nav(`/clients/${cid}`)} className="text-sm text-marine/80 hover:text-marine flex items-center gap-1 mb-4"><Icons.ArrowLeft size={16} className="text-marine" /> Retour</button>
      <PageTitle overline={`${p.type_label} · ${p.client_name}`} title={p.name} />

      <h2 className="font-display text-xl text-marine mb-4">Étapes</h2>
      {/* Lot 7 : regroupement par phase (extension de périmètre assumée, pas une
          conséquence du retrait du pont — voir le message de commit). Même structure
          que ClientProjectDetail.js, mais sans locked/isGate ni message de verrou :
          cet écran reste en lecture seule, il n'y a rien à gater. */}
      <div className="space-y-6 mb-10">
        {(p.phases || []).map((ph) => (
          <Card key={ph.key}>
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h3 className="font-display text-lg text-marine">{ph.name}</h3>
              <span className="text-xs uppercase tracking-wider text-marine/80">{PHASE_STATUS[ph.status]?.label}</span>
            </div>
            <div className="space-y-3">
              {(ph.etapes || []).map((et) => (
                <div key={et.key} className="rounded-sm border border-marine/10 p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex flex-wrap items-center gap-2"><EtapeIcon status={et.status} /><span className="font-medium text-marine">{et.name}</span>{et.blocking && <span className="text-[9px] uppercase tracking-wider whitespace-nowrap bg-marine-50 text-marine border border-marine/30 px-1.5 py-0.5 rounded-sm" title="Les étapes suivantes de cette phase ne démarrent pas tant que celle-ci n'est pas validée">Étape obligatoire</span>}</div>
                    {/* Décision F1 (18/08) : date et pastille sous le libellé sur téléphone et tablette (pl-6 = icône 16 px + écart 8 px), à droite sur ordinateur. */}
                    <div className="flex items-center gap-3 basis-full pl-6 lg:basis-auto lg:pl-0">
                      {et.planned_date && <span className="text-xs text-marine/80">{fmtDate(et.planned_date)}</span>}
                      <Badge status={et.status} />
                    </div>
                  </div>
                  {et.validated_at && <div className="text-xs text-marine/80 mt-1">Validée le {fmtDate(et.validated_at)} {validatedByLabel(et, "collab")}</div>}
                  {et.description && <div className="text-xs text-marine/80 mt-2 whitespace-pre-line">{et.description}</div>}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl text-marine">Livrables</h2>
        {p.can_upload && (
          <button onClick={() => fileRef.current.click()} disabled={busy} data-testid="collab-upload-file" className="bg-marine text-white rounded-sm px-4 py-2 text-sm hover:bg-marine-hover transition-colors flex items-center gap-1.5 disabled:opacity-60">
            <Icons.Upload size={16} /> {busy ? "Téléversement…" : "Ajouter un fichier"}
          </button>
        )}
        <input ref={fileRef} type="file" className="hidden" onChange={upload} />
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mb-10">
        {(p.deliverables || []).length === 0 && <p className="text-sm text-marine/80">{p.can_upload ? "Aucun livrable. Utilisez « Ajouter un fichier » pour téléverser." : "Aucun livrable."}</p>}
        {(p.deliverables || []).map((d) => (
          <div key={d.id} className="bg-sable rounded-sm border border-marine/10 p-4 flex items-center justify-between">
            <span className="text-sm flex items-center gap-2 truncate"><Icons.FileText size={16} className="text-marine shrink-0" /> {d.name}</span>
            <AuthFileLink path={d.path} className="text-marine shrink-0 ml-2 inline-flex"><Icons.Download size={16} /></AuthFileLink>
          </div>
        ))}
      </div>

      {/* Plus de conversation du projet ici : elle reste entre l'entreprise et Metara (arbitrage n° 2 du 01/10). */}
    </div>
  );
}
